import React, { useEffect } from 'react'
import { useOverflowObserver } from '../_hooks/OverflowObserver'
import { parseFilename } from '../_utils/filename'
import type { Props } from '../_utils/types'
import {
  StyledCaptionText,
  StyledMultilineCaption,
  StyledPlaceholderCaption,
  StyledSingleLineCaption,
  StyledSinglelineCaptionBody,
  StyledSinglelineCaptionEnding,
} from './Thumbnail.styles'
import type {
  CaptionProps,
  MultilineCaptionProps,
  SinglelineCaptionProps,
} from './Thumbnail.types'

function MultilineCaption({
  ariaHidden,
  disabled,
  caption,
  error,
  layout,
  qa,
  onCaptionOverflowChange,
  ...props
}: MultilineCaptionProps & Props) {
  const { ref, isOverflowing } = useOverflowObserver()

  useEffect(() => {
    onCaptionOverflowChange?.(isOverflowing)
  }, [isOverflowing, onCaptionOverflowChange])

  return (
    <span data-qa={qa} aria-hidden={ariaHidden ? true : undefined}>
      <StyledMultilineCaption
        ref={ref}
        $disabled={disabled}
        $error={error}
        $layout={layout}
        {...props}
      >
        <StyledCaptionText>{caption}</StyledCaptionText>
      </StyledMultilineCaption>
    </span>
  )
}

function SinglelineCaption({
  ariaHidden,
  caption,
  error,
  disabled,
  layout,
  qa,
  onCaptionOverflowChange,
  ...props
}: SinglelineCaptionProps & Props) {
  const { ref, isOverflowing } = useOverflowObserver()
  const { filename, extension, isFilename } = parseFilename(caption)

  useEffect(() => {
    onCaptionOverflowChange?.(isOverflowing)
  }, [isOverflowing, onCaptionOverflowChange])

  return (
    <StyledSingleLineCaption
      aria-hidden={ariaHidden ? true : undefined}
      $error={error}
      $disabled={disabled}
      $layout={layout}
      data-qa={qa}
      {...props}
    >
      <StyledSinglelineCaptionBody ref={ref}>
        {isFilename ? `${filename}.` : caption}
      </StyledSinglelineCaptionBody>
      {isFilename && (
        <StyledSinglelineCaptionEnding>
          {extension}
        </StyledSinglelineCaptionEnding>
      )}
    </StyledSingleLineCaption>
  )
}

export function ThumbnailCaption({
  ariaHidden,
  layout,
  disabled = false,
  error = false,
  caption,
  hasCaptionPlaceholder,
  multiline,
  qa = '',
  ...props
}: CaptionProps) {
  if (!caption) {
    const captionPlaceholder = hasCaptionPlaceholder && '- -'

    if (captionPlaceholder) {
      return (
        <StyledPlaceholderCaption
          aria-hidden={ariaHidden ? true : undefined}
          $disabled={disabled}
          $error={error}
          $layout={layout}
          data-qa={qa}
        >
          <StyledCaptionText>{captionPlaceholder}</StyledCaptionText>
        </StyledPlaceholderCaption>
      )
    }

    return null
  }

  if (multiline) {
    return (
      <MultilineCaption
        ariaHidden={ariaHidden}
        disabled={disabled}
        caption={caption}
        error={error}
        layout={layout}
        qa={qa}
        onCaptionOverflowChange={props.onCaptionOverflowChange}
        {...props}
      />
    )
  }

  return (
    <SinglelineCaption
      ariaHidden={ariaHidden}
      caption={caption}
      disabled={disabled}
      error={error}
      layout={layout}
      qa={qa}
      onCaptionOverflowChange={props.onCaptionOverflowChange}
      {...props}
    />
  )
}
